<template>
  <article
    class="affiliate-page"
    :class="{ 'affiliate-page--dark': $vuetify.theme.dark }"
    lang="en"
    aria-labelledby="affiliate-title"
  >
    <div class="affiliate-layout">
      <aside class="affiliate-index">
        <details :open="$vuetify.breakpoint.mdAndUp">
          <summary>On this page</summary>
          <nav aria-label="Affiliate program sections">
            <a href="#philosophy">Small contributions. Shared possibilities.</a>
            <a href="#how-it-works">How it works</a>
            <a href="#earnings">What could you earn?</a>
            <a href="#community">Who is it for?</a>
            <a href="#connections">It begins with a memory</a>
            <a href="#together">Support the platform. Help it grow.</a>
            <a href="#tools">Tools for your progress</a>
            <a href="#questions">Affiliate questions</a>
            <a href="#begin">Help someone begin their story</a>
          </nav>
        </details>
      </aside>
      <div class="affiliate-body">
        <header>
          <p class="affiliate-eyebrow">
            Grow the community
          </p>
          <h1 id="affiliate-title">
            Affiliate Program
          </h1>
          <p class="affiliate-lead">
            Your subscription helps support Self Biography. Our Affiliate Program lets you take your participation to a whole new level.
          </p>
          <p>Self Biography is more than a place to preserve memories. It is a growing worldwide community built around people, their lives, their accomplishments, and the stories they want to share with future generations.</p>
          <p>We believe that a community built by its members should also create opportunities for those who help it grow.</p>
          <p>That&#x27;s why we created the Self Biography Affiliate Program.</p>
          <p>Introduce your family, friends, colleagues, professional networks, or your own audience to Self Biography. When someone you refer becomes a qualifying paid subscriber, you can earn a 25% real-money commission under the program&#x27;s terms.</p>
          <p>One person. One family. One community at a time.</p>
          <p>And the possibilities grow from there.</p>
          <router-link
            class="affiliate-cta"
            to="/referrals"
          >
            Become an affiliate →
          </router-link>
        </header>
        <section
          class="affiliate-section"
          aria-labelledby="philosophy"
        >
          <h2 id="philosophy">
            Small contributions. Shared possibilities.
          </h2>
          <p>Self Biography was created with a simple economic philosophy.</p>
          <p>We don&#x27;t want to build a platform that depends primarily on advertising or exploiting people&#x27;s personal information to finance itself.</p>
          <p>Instead, we want to build a system that can support itself through the people who find value in it.</p>
          <p>Starting at just $2 a month, subscriptions help finance the infrastructure, storage, security, artificial intelligence, development, and long-term preservation that make Self Biography possible.</p>
          <p>Individually, a small monthly contribution may seem modest.</p>
          <p>But when thousands—and eventually millions—of people participate, those small contributions can become a powerful source of funding.</p>
          <p class="affiliate-statement">
            A little from each of us can build something substantial for all of us.
          </p>
          <p>And while subscriptions support the platform, the Affiliate Program creates another opportunity: sharing financially in the growth you help generate.</p>
        </section>
        <section
          class="affiliate-section"
          aria-labelledby="how-it-works"
        >
          <h2 id="how-it-works">
            How it works
          </h2>
          <h3>1. Become a member</h3>
          <p>Join Self Biography, begin building your own Life Puzzle, and discover what the platform can do for you.</p>
          <p>{{ applicationRequirement }}</p>
          <h3>2. Apply for your Affiliate account</h3>
          <p>Apply from your Referrals &amp; affiliates page. Once your application is approved, you receive your personal referral link.</p>
          <h3>3. Introduce Self Biography to others</h3>
          <p>Share your link with friends, family, professional contacts, creators, associations, organizations, or your online audience.</p>
          <p>Tell people why preserving their memories and accomplishments matters—and invite them to begin their own biographies.</p>
          <h3>4. Earn on eligible purchases</h3>
          <p>Earn a 25% commission on eligible purchases by your referred customers, including subscriptions, renewals, upgrades and AI credit packs, subject to the Affiliate Program terms. There is no referral limit. Attribution also follows friends invited by your referred customers, and their friends, at every level, back to you as the originating affiliate.</p>
          <h3>5. Follow your progress</h3>
          <p>Your Affiliate Dashboard lets you follow qualifying referrals, commissions, and payment activity. It also shows your commission period, payment hold and minimum payout.</p>
          <p class="affiliate-statement">
            You introduce the people. We provide the platform. Together, we build the community.
          </p>
        </section>
        <section
          class="affiliate-section"
          aria-labelledby="earnings"
        >
          <h2 id="earnings">
            What could you earn?
          </h2>
          <p>The following examples illustrate a 25% commission on a single month&#x27;s eligible subscription payments. They are not income forecasts or promises of recurring commissions.</p>
          <p class="affiliate-statement">
            For example, if 100 people you refer subscribe to the $9 Professional plan, that represents $225 in commissions on one month&#x27;s qualifying subscription payments: 100 × $9 × 25% = $225.
          </p>
          <p>The amount you actually earn depends on eligible subscriptions, successful payments, refunds, cancellations, and the Affiliate Program terms.</p>
          <p>The real opportunity is not limited to a single referral.</p>
          <p>You may introduce a family, a professional network, a creative community, or an audience that shares your interest in preserving people&#x27;s lives and accomplishments.</p>
          <p>Your opportunity can grow as the community grows.</p>
        </section>
        <section
          class="affiliate-section"
          aria-labelledby="community"
        >
          <h2 id="community">
            Who is it for?
          </h2>
          <p>You only need to believe that people&#x27;s lives and stories deserve to be preserved—and be willing to introduce the idea to others.</p>
          <p>The Affiliate Program is for:</p>
          <ul>
            <li>Individuals and families who want to help preserve family histories and introduce relatives and friends.</li>
            <li>Artists, writers, photographers, and creators who want to share Self Biography with their audiences.</li>
            <li>Professionals and entrepreneurs who recognize the value of preserving careers, accomplishments, and professional portfolios.</li>
            <li>Historians, genealogists, and educators who work with personal histories and cultural heritage.</li>
            <li>Community organizers and associations who want to help members preserve their stories.</li>
            <li>Content creators and publishers who can introduce Self Biography to larger audiences.</li>
          </ul>
          <p>You can start with one person or reach thousands.</p>
          <p>Everyone&#x27;s life deserves a biography. Everyone can help someone begin one.</p>
        </section>
        <section
          class="affiliate-section"
          aria-labelledby="connections"
        >
          <h2 id="connections">
            It begins with a memory
          </h2>
          <p>Imagine sharing an old family photograph.</p>
          <p>Someone recognizes a face. Another remembers the occasion. A third person has another photograph from the same day.</p>
          <p>One Memory Bit becomes a connection between several lives.</p>
          <p>Now imagine introducing those people to Self Biography, where they can begin preserving and connecting their own histories.</p>
          <p>That&#x27;s how a network of private networks can grow—naturally, through memories, relationships, families, professional connections, and communities.</p>
          <p>Sharing Self Biography is not just promoting a product.</p>
          <p>You are inviting people to preserve something that might otherwise disappear.</p>
          <p>And when those introductions result in qualifying paid subscriptions, the Affiliate Program can reward your contribution to that growth.</p>
        </section>
        <section
          class="affiliate-section"
          aria-labelledby="together"
        >
          <h2 id="together">
            Support the platform. Help it grow.
          </h2>
          <p>We see subscriptions and affiliate participation as two complementary parts of the same economic model.</p>
          <h3>SUBSCRIBE — HELP SUPPORT THE PLATFORM</h3>
          <p>Your subscription helps finance Self Biography&#x27;s technology, development, and long-term preservation.</p>
          <h3>AFFILIATE — HELP GROW THE PLATFORM</h3>
          <p>Your referrals can introduce new members, new stories, and new communities—and create an opportunity for you to earn commissions.</p>
          <p>As Self Biography develops, we envision additional possibilities involving professional connections, creative collaboration, commerce, marketplaces, and other services built around people and their voluntarily shared histories.</p>
          <p>Those are future possibilities, not current Affiliate Program benefits.</p>
          <p>The foundation remains the same: a community where people can learn about one another through their lives and memories, rather than simply through their opinions.</p>
        </section>
        <section
          class="affiliate-section"
          aria-labelledby="tools"
        >
          <h2 id="tools">
            Tools for your progress
          </h2>
          <p>Participating Affiliates have tools that make sharing and tracking straightforward:</p>
          <ul>
            <li>A personal referral link.</li>
            <li>A dashboard showing qualifying referrals and commissions.</li>
            <li>Information about subscription plans and new features.</li>
            <li>Commission and payment history.</li>
            <li>Support and documentation for Affiliate members.</li>
          </ul>
          <p>As the program grows, additional tools may be introduced to support creators, professional communities, and larger organizations.</p>
        </section>
        <section
          class="affiliate-section"
          aria-labelledby="questions"
        >
          <h2 id="questions">
            Affiliate questions
          </h2>
          <details class="affiliate-question">
            <summary>Do I need a paid subscription to become an Affiliate?</summary>
            <p>{{ applicationRequirement }} Approval is required before your affiliate link becomes available.</p>
          </details>
          <details class="affiliate-question">
            <summary>How much can I earn?</summary>
            <p>The initial commission is 25% of eligible purchases, including subscriptions and AI credit packs. Earnings depend on successful eligible payments and the terms associated with each referred customer.</p>
          </details>
          <details class="affiliate-question">
            <summary>Do I earn money just for inviting someone?</summary>
            <p>No. Commissions are based on eligible purchases, not simply sending invitations, obtaining free registrations, or recruiting other Affiliates.</p>
          </details>
          <details class="affiliate-question">
            <summary>Will commissions continue every month?</summary>
            <p>Eligible renewals can generate commissions during the commission period shown in your dashboard. Terms are fixed when each customer joins; commissions do not continue indefinitely.</p>
          </details>
          <details class="affiliate-question">
            <summary>Can I share Self Biography with people in other countries?</summary>
            <p>Our ambition is worldwide participation. Affiliate availability, payment methods, and eligibility may vary by country.</p>
          </details>
          <details class="affiliate-question">
            <summary>Are Affiliate commissions the same as AI credits?</summary>
            <p>No. Affiliate commissions are real-money rewards for eligible purchases. AI credits are used for AI features within Self Biography. A customer who joins through your affiliate link can receive the same AI credit bonus as Invite a Friend after their first subscription payment and the applicable hold period.</p>
          </details>
          <details class="affiliate-question">
            <summary>Where can I check payment details?</summary>
            <p>Your Affiliate Dashboard shows your payment hold, minimum payout, commission status and payout history. Contact Affiliate Support for questions about payment arrangements and availability in your country.</p>
          </details>
        </section>
        <section
          class="affiliate-section"
          aria-labelledby="begin"
        >
          <h2 id="begin">
            Help someone begin their story
          </h2>
          <p>Every person who joins Self Biography brings another life into our shared human history.</p>
          <p>Every family brings another collection of memories.</p>
          <p>Every creator, professional, and community brings new experiences, knowledge, and accomplishments worth preserving.</p>
          <p>Your subscription helps make the platform possible.</p>
          <p>Your participation helps make it meaningful.</p>
          <p>Your referrals can help take it to another level.</p>
          <p class="affiliate-statement">
            The sky isn&#x27;t the limit.
          </p>
          <div class="affiliate-actions">
            <router-link
              class="affiliate-cta"
              to="/auth"
            >
              Join Self Biography →
            </router-link>
            <router-link to="/pages/pricing">
              Explore subscriptions →
            </router-link>
            <router-link to="/referrals">
              Become an affiliate →
            </router-link>
          </div>
          <p>Commission rates, eligibility, referral attribution, payment conditions and availability are subject to the Affiliate Program terms. No income or earnings are guaranteed.</p>
        </section>
        <footer class="affiliate-links">
          <router-link to="/pages/support?topic=Affiliate">
            Affiliate support →
          </router-link>
          <router-link to="/pages/documentation/referrals">
            Referral documentation →
          </router-link>
          <a href="#affiliate-title">Back to top ↑</a>
        </footer>
      </div>
    </div>
  </article>
</template>
<script>
import referrals from '@/services/referrals'

export default {
  name: 'SelfBiographyAffiliateProgram',
  data: () => ({ previousTitle: '', paidSubscriptionRequired: null }),
  computed: {
    applicationRequirement() {
      if (this.paidSubscriptionRequired === false) return 'No paid subscription is required to apply. You can apply with a Free account.'
      if (this.paidSubscriptionRequired === true) return 'An active paid subscription is required when applying. Personal starts at $2/month, Professional is $9/month, and Business Plus is $15/month. Later plan changes do not affect your affiliate status or commissions.'

      return 'Visit Referrals & affiliates to check your application eligibility.'
    },
  },
  mounted() {
    referrals.applicationPolicy().then(policy => { this.paidSubscriptionRequired = policy.affiliate_subscription_required }).catch(() => { /* Eligibility remains available on the application page. */ })
    this.previousTitle = document.title
    document.title = 'Affiliate Program | Self Biography'
    this.$nextTick(() => {
      const id = this.$route.hash.slice(1)
      const target = ['affiliate-title', 'philosophy', 'how-it-works', 'earnings', 'community', 'connections', 'together', 'tools', 'questions', 'begin'].includes(id) && document.getElementById(id)
      if (target) target.scrollIntoView()
      else window.scrollTo(0, 0)
    })
  },
  beforeDestroy() { document.title = this.previousTitle },
}
</script>
<style lang="scss" scoped>
.affiliate-page {
  --affiliate-text: #5e5669; --affiliate-heading: #393345; --affiliate-accent: #713ac7; --affiliate-surface: #fff; --affiliate-tint: #f3eefb; --affiliate-divider: rgba(94,86,105,.16);
  max-width: 1160px; margin: 24px auto 48px; color: var(--affiliate-text); font-size: 16px; line-height: 1.8; text-align: left;
  &--dark { --affiliate-text: #d0cadf; --affiliate-heading: #eee9fa; --affiliate-accent: #c6a5ff; --affiliate-surface: #312d4b; --affiliate-tint: #39304f; --affiliate-divider: rgba(231,227,252,.16); }
  h1,h2,h3 { color: var(--affiliate-heading); font-weight: 600; line-height: 1.35; text-align: left; scroll-margin-top: 88px; }
  h1 { font-size: clamp(2rem,3.7vw,3rem); letter-spacing: -.035em; margin-bottom: 24px; }
  h2 { font-size: clamp(1.4rem,2.3vw,1.8rem); letter-spacing: -.02em; margin-bottom: 22px; }
  h3 { font-size: 1.25rem; margin-bottom: 18px; }
  h3 span { color: var(--affiliate-accent); font-size: 1rem; }
  p { margin-bottom: 18px; }
  strong { font-weight: 600; }
  a { color: var(--affiliate-accent); text-underline-offset: 4px; }
  a:focus-visible, summary:focus-visible { outline: 2px solid var(--affiliate-accent); outline-offset: 4px; }
  .affiliate-eyebrow { color: var(--affiliate-accent); font-size: .75rem; font-weight: 600; letter-spacing: .08em; text-transform: uppercase; margin-bottom: 14px; }
}
.affiliate-layout { display: grid; grid-template-columns: 200px minmax(0,1fr); gap: 32px; align-items: start; }
.affiliate-index { position: sticky; top: 88px; }
.affiliate-index summary { font-weight: 600; cursor: pointer; min-height: 44px; }
.affiliate-index nav { display: flex; flex-direction: column; border-left: 1px solid var(--affiliate-divider); padding-left: 16px; }
.affiliate-index a { display: flex; align-items: center; min-height: 44px; padding: 6px 0; font-size: .875rem; line-height: 1.5; text-decoration: none; }
.affiliate-index a:hover { text-decoration: underline; }
.affiliate-body { min-width: 0; background: var(--affiliate-surface); border: 1px solid var(--affiliate-divider); border-radius: 8px; padding: 44px; }
.affiliate-lead { font-size: 1.2rem; color: var(--affiliate-heading); }
.affiliate-section { margin-top: 30px; padding-top: 34px; border-top: 1px solid var(--affiliate-divider); }
.affiliate-statement { color: var(--affiliate-accent); padding: 20px 24px; background: var(--affiliate-tint); border-radius: 6px; margin: 24px 0; }
.affiliate-links { display: flex; flex-wrap: wrap; gap: 12px 24px; margin-top: 32px; padding-top: 24px; border-top: 1px solid var(--affiliate-divider); }
.affiliate-links a { display: inline-flex; align-items: center; min-height: 44px; font-size: .875rem; }
@media(max-width:959px) {
  .affiliate-layout { grid-template-columns: minmax(0,1fr); gap: 20px; }
  .affiliate-index { position: static; }
  .affiliate-index summary { padding: 8px 0; }
  .affiliate-body { padding: 28px; }
}
@media(max-width:599px) {
  .affiliate-page { margin: 0 auto 32px; }
  .affiliate-body { padding: 24px 0 0; border: 0; border-top: 1px solid var(--affiliate-divider); border-radius: 0; background: transparent; }
  .affiliate-statement { padding: 18px 20px; }
  .affiliate-links { flex-direction: column; gap: 4px; }
}

.affiliate-page ul { padding-left: 22px; margin-bottom: 24px; }
.affiliate-page li { padding-left: 4px; margin-bottom: 12px; }
.affiliate-page h3 { margin-top: 26px; }
.affiliate-page .affiliate-cta { display: inline-flex; align-items: center; min-height: 44px; padding: 10px 18px; border-radius: 6px; background: var(--affiliate-accent); color: var(--affiliate-surface); text-decoration: none; font-weight: 600; }
.affiliate-actions { display: flex; flex-wrap: wrap; gap: 16px 24px; align-items: center; margin: 24px 0; }
.affiliate-actions a { min-height: 44px; display: inline-flex; align-items: center; }
.affiliate-question { border-bottom: 1px solid var(--affiliate-divider); padding: 16px 0; }
.affiliate-question summary { min-height: 44px; cursor: pointer; font-weight: 600; color: var(--affiliate-heading); }
.affiliate-question p { margin-top: 12px; }
</style>
